import React, { useEffect, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
// import './PurchaseOrderAccordion.css';

function CommonPurchaseOrderFields({ onDataChange, initialData }) {
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Main fields
    const [enquiryno, setEnquiryno] = useState(initialData?.enquiryno || '');
    const [enquirydate, setEnquirydate] = useState(initialData?.enquirydate || '');
    const [shipto, setShipto] = useState(initialData?.shipto || '');
    const [freight, setFreight] = useState(initialData?.freight || '');
    const [pandf, setPandf] = useState(initialData?.pandf || '');
    const [paymentterms, setPaymentterms] = useState(initialData?.paymentterms || '');
    const [deliveryterm, setDeliveryterm] = useState(initialData?.deliveryterm || '');
    const [deliverytime, setDeliverytime] = useState(initialData?.deliverytime || '');
    const [remarks, setRemarks] = useState(initialData?.remarks || '');
    const [certificate, setCertificate] = useState(initialData?.certificate || '');
    const [quotationreferencenumber, setquotationreferencenumber] = useState(initialData?.quotationreferencenumber || '');
    const [quotationreferencedate, setquotationreferencedate] = useState(initialData?.quotationreferencedate || '');

    // Supplier related fields
    const [supplierSearch, setSupplierSearch] = useState(encodeURIComponent(" "));
    const [supplier, setSupplier] = useState(initialData?.supplier || null);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [supplierDetails, setSupplierDetails] = useState({
        contactperson: initialData?.supplierDetails?.contactperson || '',
        contactno: initialData?.supplierDetails?.contactno || '',
        email: initialData?.supplierDetails?.email || '',
    });
    const [supplierAddresses, setSupplierAddresses] = useState([]);
    const [supplierMultipleAddress, setSupplierMultipleAddress] = useState(initialData?.supplierMultipleAddress || null);

    // Branch and warehouse
    const [branch, setBranch] = useState(initialData?.branch || null);
    const [branchOptions, setBranchOptions] = useState([]);
    const [warehouse, setWarehouse] = useState(initialData?.warehouse || null);
    const [warehouseOptions, setWarehouseOptions] = useState([]);

    // Accordion states
    const [isBranchWarehouseOpen, setIsBranchWarehouseOpen] = useState(false);
    const [isSupplierOpen, setIsSupplierOpen] = useState(false);

    // Search handlers with debounce
    const handleSearchChangeSupplier = debounce((inputValue) => {
        setSupplierSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 300);

    // Data fetch functions
    const getSupplierData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}supplier/page/0/10/asc/id/${supplierSearch}`,
                { headers: authHeader() }
            );

            const suppliers = response.data.Index || response.data.data || [];
            setSupplierOptions(
                suppliers.map(s => ({
                    value: s.id,
                    label: s.companyname,
                    contactperson: s.contactperson,
                    contactno: s.contactno,
                    email: s.email,
                }))
            );
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch suppliers. Please try again.',
            });
        }
    };

    const getSupplierDetails = async (id) => {
        try {
            const response = await axios.get(`${BASE_URL}supplier/findById/${id}`, {
                headers: authHeader(),
            });
            const data = response.data;
            setSupplierDetails({
                contactperson: data.contactperson,
                contactno: data.contactno,
                email: data.email,
            });
            setFreight(data.freight || '');
            setPandf(data.packingandforwarding || '');
            setPaymentterms(data.paymentterm || '');
            getSupplierAddresses(id);
        } catch (error) {
            console.error("Error fetching supplier details", error);
        }
    };

    const getSupplierAddresses = async (id) => {
        try {
            const response = await axios.get(`${BASE_URL}supplierMultipleAddress/getAddress/${id}`, {
                headers: authHeader(),
            });
            setSupplierAddresses(response.data.map(addr => ({
                value: addr.id,
                label: addr.supplieraddress,
            })));
        } catch (error) {
            console.error("Error fetching supplier addresses", error);
        }
    };

    const getBranchData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}branch/findAllBranch`, {
                headers: authHeader(),
            });
            setBranchOptions(response.data.map(b => ({ value: b.id, label: b.branchname })));
        } catch (error) {
            console.error("Error fetching branches", error);
        }
    };

    const getWarehouseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
                headers: authHeader(),
            });
            setWarehouseOptions(response.data.map(w => ({ value: w.id, label: w.warehousename })));
        } catch (error) {
            console.error("Error fetching warehouses", error);
        }
    };

    // Change handlers
    const handleSupplierChange = (selected) => {
        setSupplier(selected);
        setSupplierMultipleAddress(null);
        if (selected) {
            getSupplierDetails(selected.value);
            setIsSupplierOpen(true); // Auto-expand when supplier is selected
        } else {
            setSupplierDetails({
                contactperson: '',
                contactno: '',
                email: '',
            });
            setSupplierAddresses([]);
            setFreight('');
            setPandf('');
            setPaymentterms('');
        }
    };

    const handleSupplierAddressChange = (selected) => {
        setSupplierMultipleAddress(selected);
    };

    const handleWarehouseChange = async (selected) => {
        setWarehouse(selected);
        if (selected) {
            try {
                const response = await axios.get(`${BASE_URL}warehouse/findById/${selected.value}`, {
                    headers: authHeader(),
                });
                setShipto(response.data.address || '');
            } catch (error) {
                console.error("Error fetching warehouse address", error);
            }
        } else {
            setShipto('');
        }
    };

    const handleBranchChange = (selected) => {
        setBranch(selected);
    };

    // Effect hooks
    useEffect(() => {
        getBranchData();
        getWarehouseData();
        if (initialData?.supplier?.value) {
            getSupplierDetails(initialData.supplier.value);
        }
    }, []);

    useEffect(() => {
        getSupplierData();
    }, [supplierSearch]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                enquiryno,
                enquirydate,
                shipto,
                freight,
                pandf,
                paymentterms,
                deliveryterm,
                supplier,
                supplierDetails,
                supplierMultipleAddress,
                branch,
                warehouse,
                deliverytime,
                remarks,
                certificate,
                quotationreferencenumber,
                quotationreferencedate
            });
        }
    }, [
        enquiryno, enquirydate, shipto, freight, pandf,
        paymentterms, deliveryterm, supplier, supplierDetails, supplierMultipleAddress,
        branch, warehouse, deliverytime,
        remarks,
        certificate,
        quotationreferencenumber,
        quotationreferencedate
    ]);

    const toggleBranchWarehouseSection = () => {
        setIsBranchWarehouseOpen(!isBranchWarehouseOpen);
    };

    const toggleSupplierSection = () => {
        setIsSupplierOpen(!isSupplierOpen);
    };

    return (
        <>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Enquiry No.</Form.Label>
                        <Form.Control
                            type="text"
                            value={enquiryno}
                            onChange={(e) => setEnquiryno(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Enquiry Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={enquirydate}
                            onChange={(e) => setEnquirydate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>



            {/* Supplier Accordion */}
            <div className="po-accordion-container">
                <div
                    className={`po-accordion-header ${isSupplierOpen ? 'po-accordion-open' : ''}`}
                    onClick={toggleSupplierSection}
                >
                    <h7>Supplier Information</h7>
                    <span className="po-accordion-icon">
                        {isSupplierOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`po-accordion-content ${isSupplierOpen ? 'po-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Supplier <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={supplier}
                                    onChange={handleSupplierChange}
                                    onInputChange={handleSearchChangeSupplier}
                                    options={supplierOptions}
                                    placeholder="Search supplier"
                                    className={formErrors.supplier ? 'is-invalid' : ''}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Contact Person</Form.Label>
                                <Form.Control
                                    value={supplierDetails.contactperson}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Contact Number</Form.Label>
                                <Form.Control
                                    value={supplierDetails.contactno}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    value={supplierDetails.email}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Supplier Address <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={supplierMultipleAddress}
                                    onChange={handleSupplierAddressChange}
                                    options={supplierAddresses}
                                    placeholder="Select address"
                                    isClearable
                                    className={formErrors.supplierMultipleAddress ? 'is-invalid' : ''}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Packing & Forwarding (%)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={pandf}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val >= 0 && val <= 100) setPandf(val);
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Freight (%)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={freight}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val >= 0 && val <= 100) setFreight(val);
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Payment Terms</Form.Label>
                                <Form.Control
                                    value={paymentterms}
                                    onChange={(e) => setPaymentterms(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Branch and Warehouse Accordion */}
            <div className="po-accordion-container">
                <div
                    className={`po-accordion-header ${isBranchWarehouseOpen ? 'po-accordion-open' : ''}`}
                    onClick={toggleBranchWarehouseSection}
                >
                    <h7>Branch and Warehouse Information</h7>
                    <span className="po-accordion-icon">
                        {isBranchWarehouseOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`po-accordion-content ${isBranchWarehouseOpen ? 'po-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Branch</Form.Label>
                                <Select
                                    value={branch}
                                    onChange={handleBranchChange}
                                    options={branchOptions}
                                    placeholder="Select branch"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Warehouse</Form.Label>
                                <Select
                                    value={warehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder="Select warehouse"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Delivery Address:</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={shipto}
                                    onChange={(e) => setShipto(e.target.value)}
                                    placeholder="Shipping address"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Additional Fields */}
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Terms</Form.Label>
                        <Form.Control
                            value={deliveryterm}
                            onChange={(e) => setDeliveryterm(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Time</Form.Label>
                        <Form.Control
                            value={deliverytime}
                            onChange={(e) => setDeliverytime(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Certificate</Form.Label>
                        <Form.Control
                            value={certificate}
                            onChange={(e) => setCertificate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Quotation Reference Number</Form.Label>
                        <Form.Control
                            value={quotationreferencenumber}
                            onChange={(e) => setquotationreferencenumber(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Quotation Reference Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={quotationreferencedate}
                            onChange={(e) => setquotationreferencedate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
}

export default CommonPurchaseOrderFields;